import { createContext, type MutableRefObject, useContext } from 'react';

/**
 * Remembers which tree row's info tooltip is currently open, so it survives the
 * tree's row remounts. The selection tree deliberately remounts every row on
 * each render (see TreeSelection — its inline row renderer is a fresh component
 * type each render so selection changes reach the rows), which tears down the
 * hovered, portaled tooltip. Toggling a company from inside the tooltip is a
 * state change, so it would remount the row and close the tooltip before the
 * user sees the "+" flip to "✓".
 *
 * The open node id lives in a ref held by a component that does NOT remount (the
 * popout tree container), so writing it never re-renders. A remounted tooltip
 * reads the ref to decide its initial open state and reappears open, matching
 * the checkbox-animation workaround already used for the same remount churn.
 */
export type EntityInfoOpenStore = {
  /** Node id of the tooltip that should be open across a row remount. */
  openIdRef: MutableRefObject<string | null>;
  /** rcid of the tooltip whose "View Competitors" list is expanded, so the
   * expanded list also survives a remount (e.g. after adding a competitor). */
  expandedRcidRef: MutableRefObject<number | null>;
  /** rcid whose card already reported its parent chip during this open, so
   * the remount after a toggle doesn't report it again. */
  reportedParentRcidRef: MutableRefObject<number | null>;
};

const EntityInfoOpenContext = createContext<EntityInfoOpenStore | null>(null);

export const EntityInfoOpenProvider = EntityInfoOpenContext.Provider;

/** The tooltip open-persistence store, or null outside a provider (where the
 * tooltip isn't in a remounting tree and needs no persistence). */
export const useEntityInfoOpenStore = (): EntityInfoOpenStore | null =>
  useContext(EntityInfoOpenContext);
